import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ALL_ATHLETES,
  Athlete,
  Discipline,
  DISCIPLINE_LABELS,
  DISCIPLINE_COLORS,
} from '../data/athletes';
import { getAllAthleteProfiles, AthleteProfile } from '../firebase/athleteService';

function toDriveEmbed(url: string): string {
  const openMatch = url.match(/[?&]id=([^&]+)/);
  const fileMatch = url.match(/\/file\/d\/([^/]+)/);
  const id = openMatch?.[1] ?? fileMatch?.[1];
  if (id) return `https://lh3.googleusercontent.com/d/${id}`;
  return url;
}

const CLASS_ORDER = ['AL1','AL2','AU3','B1','B2','B3','RP1','RP2','RP3'];

type Filter = 'all' | Discipline;

const DisciplineBadge: React.FC<{ d: Discipline }> = ({ d }) => (
  <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${DISCIPLINE_COLORS[d]}`}>
    {DISCIPLINE_LABELS[d]}
  </span>
);

const AthleteCard: React.FC<{ athlete: Athlete; pictureUrl?: string; onClick: () => void }> = ({ athlete, pictureUrl, onClick }) => {
  const [imgError, setImgError] = useState(false);
  return (
    <button
      onClick={onClick}
      className="bg-white rounded-xl shadow-sm border border-gray-100 text-left hover:shadow-md hover:border-gray-200 transition-all w-full overflow-hidden"
    >
      {/* Portrait image */}
      <div className="w-full aspect-[3/4] bg-gray-100 overflow-hidden">
        {pictureUrl && !imgError ? (
          <img
            src={pictureUrl}
            alt={`${athlete.firstName} ${athlete.lastName}`}
            onError={() => setImgError(true)}
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <span className="text-6xl">{athlete.flag}</span>
          </div>
        )}
      </div>
      {/* Info */}
      <div className="p-3">
        <p className="font-bold text-dark leading-tight text-sm">
          {athlete.firstName} {athlete.lastName}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">{athlete.nationality} · {athlete.classId}</p>
        <div className="flex flex-wrap gap-1 mt-2">
          {athlete.disciplines.map(d => <DisciplineBadge key={d} d={d} />)}
        </div>
      </div>
    </button>
  );
};

const AthletesPage: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const [profiles, setProfiles] = useState<Record<string, AthleteProfile>>({});

  useEffect(() => {
    getAllAthleteProfiles().then(setProfiles);
  }, []);

  const filtered = ALL_ATHLETES
    .filter(a => filter === 'all' || a.disciplines.includes(filter as Discipline))
    .sort((a, b) => {
      const ci = CLASS_ORDER.indexOf(a.classId) - CLASS_ORDER.indexOf(b.classId);
      if (ci !== 0) return ci;
      return `${a.lastName}${a.firstName}`.localeCompare(`${b.lastName}${b.firstName}`);
    });

  const countFor = (d: Discipline) => ALL_ATHLETES.filter(a => a.disciplines.includes(d)).length;

  const tabs: { label: string; value: Filter }[] = [
    { label: `All (${ALL_ATHLETES.length})`, value: 'all'    },
    { label: `⚡ Speed (${countFor('speed')})`,     value: 'speed'   },
    { label: `🪨 Boulder (${countFor('boulder')})`, value: 'boulder' },
    { label: `🧗 Lead (${countFor('lead')})`,       value: 'lead'    },
  ];

  return (
    <div className="min-h-screen bg-dark text-white">

      {/* Header */}
      <div className="px-4 pt-10 pb-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/results')}
            className="flex items-center gap-1.5 text-blue-300 hover:text-white text-sm mb-6 transition-colors"
          >
            ← Results hub
          </button>
          <h1 className="text-4xl font-extrabold tracking-tight mb-1">Athletes</h1>
          <p className="text-blue-300 text-lg">BK 2026 · Belgian Paraclimbing Festival</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-12">

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-1 mb-6 bg-white/10 rounded-xl p-1 w-fit">
          {tabs.map(t => (
            <button
              key={t.value}
              onClick={() => setFilter(t.value)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                filter === t.value
                  ? 'bg-white text-dark shadow'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map(a => {
            const rawUrl = profiles[a.id]?.pictureUrl;
            const pictureUrl = rawUrl ? toDriveEmbed(rawUrl) : undefined;
            return (
              <AthleteCard
                key={a.id}
                athlete={a}
                pictureUrl={pictureUrl}
                onClick={() => navigate(`/athletes/${a.id}`)}
              />
            );
          })}
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-white/50 py-12">No athletes found.</p>
        )}
      </div>
    </div>
  );
};

export default AthletesPage;
